import { FC } from 'react';
import moment from 'moment';
import { RadioGroup, Radio, Form } from 'rsuite';
import { useCampaignStore } from 'store/campaignStore';
import { ActionType, RunningPattern } from 'core/rtbCampaign/singlePage/common';
import FutureDateRangePicker from 'components/DateRangePicker/FutureDateRangePicker';
import RsTooltip from 'components/Rsuite/Tooltip/Tooltip';
import styles from '../RunningPatternMode/runningPatternMode.module.scss';
import { RsDateRangePicker } from 'components/Rsuite/DateRangePicker';

interface RunningPatternModeProps {
  selected: RunningPattern;
  diffDays: number;
  defaultRunningPattern?: string;
  runningPattern?: string;
  onChange: (s: RunningPattern) => void;
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  onChangeStartDate: (date: any) => void;
  onChangeEndDate: (date: any) => void;
  minDate?: string;
  maxDate?: string;
  className?: string;
  dataRangeError?: string;
  startDatePickerDisabled?: boolean;
  disabled?: boolean;
  disabledText?: string;
}

export const RunningPatternMode: FC<RunningPatternModeProps> = ({
  selected = '',
  diffDays,
  defaultRunningPattern,
  runningPattern,
  onChange = () => undefined,
  startDate = moment().format('YYYY-MM-DD HH:mm:ss'),
  endDate = moment().format('YYYY-MM-DD HH:mm:ss'),
  onDateRangeChange = () => undefined,
  onChangeStartDate = () => undefined,
  onChangeEndDate = () => undefined,
  minDate,
  maxDate,
  startDatePickerDisabled = false,
  dataRangeError,
  disabled = false,
  disabledText = '',
}) => {
  const { actionType } = useCampaignStore();
  const handleChange = (v) => {
    onChange(v);
  };

  const handleDayRangeChange = (start, end) => {
    onDateRangeChange(start, end);
  };

  const handleChangeStartDate = (date) => {
    onChangeStartDate(date);
  };

  const handleChangeEndDate = (date) => {
    onChangeEndDate(date);
  };

  return (
    <RadioGroup
      value={selected}
      onChange={handleChange}
      className={styles['radio-group-wrapper']}
      inline
    >
      {/* 無限制選項 */}
      <div className={styles['pattern-container']}>
        <div className={styles['pattern__item-wrapper']}>
          <Radio
            value={RunningPattern.UNLIMITED}
            disabled={disabled}
            className={styles.pattern__radio}
          >
            無限制
          </Radio>
        </div>
        <div className={styles.pattern__hint}>
          {disabledText ? (
            <Form.HelpText>{disabledText}</Form.HelpText>
          ) : (
            <div>
              <Form.HelpText>廣告會投放至廣告訂單預算用完為止</Form.HelpText>
              <Form.HelpText>
                廣告建立後，將無法更改為設定開始/結束日期
              </Form.HelpText>
            </div>
          )}
        </div>
      </div>

      {/* 設定開始/結束日期選項 */}
      <div className={styles['pattern-container']}>
        <div className={styles['pattern__item-wrapper']}>
          <Radio
            value={RunningPattern.LIMITED}
            disabled={
              actionType === 'edit' &&
              defaultRunningPattern === RunningPattern.UNLIMITED
            }
            className={styles.pattern__radio}
          >
            設定開始/結束日期
          </Radio>

          {runningPattern === RunningPattern.LIMITED && (
            <div className='relative'>
              <RsDateRangePicker
                startDate={moment(startDate).format('YYYY-MM-DD HH:mm')}
                endDate={moment(endDate).format('YYYY-MM-DD HH:mm')}
                minDate={moment(minDate).format('YYYY-MM-DD HH:mm')}
                maxDate={moment(maxDate)
                  .endOf('day')
                  .format('YYYY-MM-DD HH:mm')}
                showTimePicker={true}
                onStartDateChange={handleChangeStartDate}
                onEndDateChange={handleChangeEndDate}
                startDatePickerDisabled={startDatePickerDisabled}
                disabled={runningPattern !== RunningPattern.LIMITED}
              />
              <Form.ErrorMessage show={!!dataRangeError} placement='topEnd'>
                {dataRangeError}
              </Form.ErrorMessage>
            </div>
          )}
        </div>
        {runningPattern === RunningPattern.LIMITED && (
          <>
            <div className={`${styles.pattern__hint}`}>
              <Form.HelpText>
                建議活動至少大於兩周，以達到系統最佳學習效果
              </Form.HelpText>
              <RsTooltip>
                <span>廣告活動開始後，將無法修改開始日期</span>
              </RsTooltip>
            </div>
            <Form.HelpText className={styles.pattern__hint}>
              共 {diffDays} 天
            </Form.HelpText>
          </>
        )}
      </div>
    </RadioGroup>
  );
};
